import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
const wtrLogo = "/images/wtr-logo.webp";

const servicesDropdown = [
  { name: "Water Damage", href: "/water-damage" },
  { name: "Sewage Cleanup", href: "/sewage-cleanup" },
  { name: "Mold Remediation", href: "/mold-remediation" },
  { name: "Storm Damage", href: "/storm-damage" },
  { name: "Freeze / Burst Pipes", href: "/freeze-damage" },
  { name: "Structural Drying", href: "/structural-drying" },
  { name: "Water Extraction", href: "/water-extraction" },
  { name: "Reconstruction", href: "/reconstruction" },
  { name: "Contents Pack-Out", href: "/contents-pack-out" },
  { name: "Moisture Survey", href: "/moisture-survey" },
];

const areasDropdown = [
  { name: "New Braunfels", href: "/new-braunfels" },
  { name: "Seguin", href: "/seguin" },
  { name: "San Marcos", href: "/san-marcos" },
  { name: "Canyon Lake", href: "/canyon-lake" },
  { name: "All Service Areas", href: "/areas" },
];

const resourcesDropdown = [
  { name: "Knowledge Base", href: "/knowledge-base" },
  { name: "How to Recover from Water Damage", href: "/how-to-recover-from-water-damage" },
  { name: "Is Your Water Damage Serious?", href: "/how-to-tell-if-water-damage-is-serious" },
  { name: "How to Remove Water Damage", href: "/how-to-remove-water-damage-from-a-house" },
  { name: "What Does Water Damage Look Like?", href: "/what-does-water-damage-look-like" },
  { name: "Sleeping in a Water-Damaged Home", href: "/sleeping-in-water-damaged-home" },
  { name: "60-Minute Survival Guide", href: "/60-minute-water-damage-survival-guide" },
  { name: "Insurance Claims", href: "/insurance" },
  { name: "Our Process", href: "/process" },
  { name: "Why WTR?", href: "/why-wtr" },
];

interface DropdownItem {
  name: string;
  href: string;
}

const DesktopDropdown = ({
  label,
  href,
  items,
  isActive,
}: {
  label: string;
  href: string;
  items: DropdownItem[];
  isActive: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link
        to={href}
        className={`text-sm font-medium transition-colors hover:text-wtr-blue inline-flex items-center gap-1 ${
          isActive ? "text-wtr-blue" : "text-wtr-gray"
        }`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </Link>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[220px]">
            {items.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block px-4 py-2 text-sm text-wtr-gray hover:bg-secondary hover:text-wtr-blue transition-colors"
                onClick={() => setOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileDropdown = ({
  label,
  href,
  items,
  onNavigate,
}: {
  label: string;
  href: string;
  items: DropdownItem[];
  onNavigate: () => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Link
          to={href}
          className="text-base font-medium text-wtr-gray hover:text-wtr-blue transition-colors"
          onClick={onNavigate}
        >
          {label}
        </Link>
        <button
          onClick={() => setOpen(!open)}
          className="p-1 text-wtr-gray"
          aria-label={`Toggle ${label} submenu`}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="pl-4 mt-2 space-y-2 border-l-2 border-gray-100">
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="block text-sm text-wtr-gray hover:text-wtr-blue transition-colors"
              onClick={onNavigate}
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const closeMenu = () => setIsMenuOpen(false);

  const isServiceActive = ["/services", ...servicesDropdown.map(s => s.href)].includes(location.pathname);
  const isResourceActive = resourcesDropdown.map(r => r.href).includes(location.pathname);
  const isAreaActive = areasDropdown.map(a => a.href).includes(location.pathname);
  const isEmergencyActive = ["/emergency", "/emergency-water-damage"].includes(location.pathname);

  return (
    <nav className="bg-white shadow-soft sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={wtrLogo}
              alt="WTR Restoration"
              className="h-8 sm:h-10 md:h-12 w-auto"
              width={140}
              height={56}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <Link
              to="/emergency"
              className={`text-sm font-bold transition-colors hover:text-destructive ${
                isEmergencyActive ? "text-destructive" : "text-destructive/80"
              }`}
            >
              Emergency
            </Link>

            <DesktopDropdown
              label="Services"
              href="/services"
              items={servicesDropdown}
              isActive={isServiceActive}
            />

            <DesktopDropdown
              label="Areas"
              href="/areas"
              items={areasDropdown}
              isActive={isAreaActive}
            />

            <Link
              to="/our-work"
              className={`text-sm font-medium transition-colors hover:text-wtr-blue ${
                location.pathname === "/our-work" ? "text-wtr-blue" : "text-wtr-gray"
              }`}
            >
              Our Work
            </Link>

            <Link
              to="/blog"
              className={`text-sm font-medium transition-colors hover:text-wtr-blue ${
                location.pathname === "/blog" ? "text-wtr-blue" : "text-wtr-gray"
              }`}
            >
              Blog
            </Link>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-wtr-blue ${
                location.pathname === "/about" ? "text-wtr-blue" : "text-wtr-gray"
              }`}
            >
              About
            </Link>

            <DesktopDropdown
              label="Resources"
              href="/knowledge-base"
              items={resourcesDropdown}
              isActive={isResourceActive}
            />

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-wtr-blue ${
                location.pathname === "/contact" ? "text-wtr-blue" : "text-wtr-gray"
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <span className="text-xs font-semibold text-wtr-blue uppercase tracking-wide">24/7 Emergency</span>
            <Button
              variant="phone"
              size="sm"
              onClick={() => window.open('tel:830-360-2600')}
              className="font-semibold"
            >
              <Phone className="w-4 h-4" />
              830-360-2600
            </Button>
          </div>

          {/* Mobile: Call + Hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            <Button
              variant="phone"
              size="sm"
              onClick={() => window.open('tel:830-360-2600')}
              className="font-semibold text-xs px-3"
            >
              <Phone className="w-3.5 h-3.5" />
              Call
            </Button>
            <button
              className="p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-wtr-gray" />
              ) : (
                <Menu className="w-6 h-6 text-wtr-gray" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <Link to="/emergency" className="text-base font-bold text-destructive hover:text-destructive/80" onClick={closeMenu}>Emergency</Link>

              <MobileDropdown label="Services" href="/services" items={servicesDropdown} onNavigate={closeMenu} />

              <MobileDropdown label="Areas" href="/areas" items={areasDropdown} onNavigate={closeMenu} />

              <Link to="/our-work" className="text-base font-medium text-wtr-gray hover:text-wtr-blue" onClick={closeMenu}>Our Work</Link>
              <Link to="/blog" className="text-base font-medium text-wtr-gray hover:text-wtr-blue" onClick={closeMenu}>Blog</Link>
              <Link to="/about" className="text-base font-medium text-wtr-gray hover:text-wtr-blue" onClick={closeMenu}>About</Link>

              <MobileDropdown label="Resources" href="/knowledge-base" items={resourcesDropdown} onNavigate={closeMenu} />

              <Link to="/contact" className="text-base font-medium text-wtr-gray hover:text-wtr-blue" onClick={closeMenu}>Contact</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
